import React, { useState } from "react";
import { Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { cn } from "../../../lib/utils";

interface Url {
  path: string;
  description: string;
}

interface Props {
  urls: Url[];
}

export const DocumentExamples: React.FC<Props> = ({ urls }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const currentExample = urls[currentSlide];

  const onGoPrevExample = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const onGoNextExample = () => {
    if (currentSlide < urls.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const canGoPrev = currentSlide > 0;
  const canGoNext = currentSlide < urls.length - 1;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Eye className="h-4 w-4" />
          Ver ejemplos
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{currentExample?.description}</DialogTitle>
        </DialogHeader>

        <div className="relative">
          {/* Image Container */}
          <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-100">
            <img
              src={currentExample?.path}
              alt={currentExample?.description}
              className="h-full w-full object-contain"
            />
          </div>

          {/* Navigation Arrows */}
          <button
            type="button"
            onClick={onGoPrevExample}
            disabled={!canGoPrev}
            className={cn(
              "absolute left-2 top-1/2 -translate-y-1/2",
              "flex h-10 w-10 items-center justify-center rounded-full",
              "bg-white/90 shadow-lg backdrop-blur-sm transition-all",
              canGoPrev
                ? "text-gray-700 hover:bg-white hover:scale-105"
                : "text-gray-300 cursor-not-allowed"
            )}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={onGoNextExample}
            disabled={!canGoNext}
            className={cn(
              "absolute right-2 top-1/2 -translate-y-1/2",
              "flex h-10 w-10 items-center justify-center rounded-full",
              "bg-white/90 shadow-lg backdrop-blur-sm transition-all",
              canGoNext
                ? "text-gray-700 hover:bg-white hover:scale-105"
                : "text-gray-300 cursor-not-allowed"
            )}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex items-center justify-center gap-2 pt-2">
          {urls.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentSlide(index)}
              className={cn(
                "h-2 w-2 rounded-full transition-all",
                index === currentSlide
                  ? "w-6 bg-gray-900"
                  : "bg-gray-300 hover:bg-gray-400"
              )}
            />
          ))}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between border-t pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onGoPrevExample}
            disabled={!canGoPrev}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <span className="text-sm text-gray-500">
            {currentSlide + 1} de {urls.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onGoNextExample}
            disabled={!canGoNext}
            className="gap-1"
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
